// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { formatHumanName } from '@medplum/core';
import type { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { ProviderRoleCode } from '@polarissleep/scheduling';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '@polarissleep/scheduling';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { getProviderAppEnvConfig } from '../config/env';

/**
 * Clinic roles that can own a task. Software engineers and accounts without a
 * Polaris PractitionerRole (test users, legacy seeds) are deliberately excluded.
 */
export const ASSIGNABLE_PROVIDER_ROLES: readonly ProviderRoleCode[] = [
  'physician',
  'nurse-practitioner',
  'care-coordinator',
];

export interface AssignableProviderOption {
  /** `Practitioner/<id>` reference string. */
  value: string;
  label: string;
  practitioner: Practitioner;
  role: ProviderRoleCode;
}

export async function searchAssignableProviders(
  medplum: MedplumClient,
  organizationId: string | undefined
): Promise<AssignableProviderOption[]> {
  const search: Record<string, string> = {
    role: ASSIGNABLE_PROVIDER_ROLES.map((role: ProviderRoleCode) => `${POLARIS_PROVIDER_ROLE_SYSTEM}|${role}`).join(
      ','
    ),
    active: 'true',
    _count: '300',
  };
  if (organizationId) {
    search._compartment = `Organization/${organizationId}`;
  }

  const roles = await medplum.searchResources('PractitionerRole', search, { cache: 'no-cache' });
  const roleByPractitioner = new Map<string, ProviderRoleCode>();
  for (const practitionerRole of roles as PractitionerRole[]) {
    const reference = practitionerRole.practitioner?.reference;
    const code = practitionerRole.code
      ?.flatMap((concept) => concept.coding ?? [])
      .find((coding) => coding.system === POLARIS_PROVIDER_ROLE_SYSTEM)?.code as ProviderRoleCode | undefined;
    if (reference?.startsWith('Practitioner/') && code && ASSIGNABLE_PROVIDER_ROLES.includes(code)) {
      roleByPractitioner.set(reference, code);
    }
  }
  if (roleByPractitioner.size === 0) {
    return [];
  }

  const ids = [...roleByPractitioner.keys()].map((reference: string) => reference.replace('Practitioner/', ''));
  const practitioners = await medplum.searchResources(
    'Practitioner',
    { _id: ids.join(','), _count: String(ids.length) },
    { cache: 'no-cache' }
  );

  return practitioners
    .filter((practitioner: Practitioner) => practitioner.id && practitioner.active !== false)
    .map((practitioner: Practitioner) => {
      const value = `Practitioner/${practitioner.id}`;
      return {
        value,
        label: practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : 'Unnamed provider',
        practitioner,
        role: roleByPractitioner.get(value) as ProviderRoleCode,
      };
    })
    .sort((a: AssignableProviderOption, b: AssignableProviderOption) => a.label.localeCompare(b.label));
}

/**
 * Individual clinic providers (physicians, NPs, care coordinators) in the
 * current tenant who can be assigned as `Task.owner`.
 */
export function useAssignableProviders(): {
  providers: AssignableProviderOption[];
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const { medplumOrganizationId } = getProviderAppEnvConfig();
  const [providers, setProviders] = useState<AssignableProviderOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    searchAssignableProviders(medplum, medplumOrganizationId)
      .then((next: AssignableProviderOption[]) => {
        if (mounted) {
          setProviders(next);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        Sentry.captureException(err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load providers'));
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [medplum, medplumOrganizationId]);

  return { providers, loading, error };
}
